import React, { useEffect, useMemo, useState } from "react";
import { Icon, Field } from "@/components/kit";
import { api, isSuperadmin, fileUrlAuth } from "@/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

const eur = (n) => (n == null || n === "" ? "—" : Math.round(Number(n)).toLocaleString("fr-FR") + " €");
const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Taux de TVA disponibles selon le pays de l'indicatif téléphonique (miroir des règles backend).
const PHONE_VAT_TABLE = [
  ["+352", "LU", 3], ["+33", "FR", 10], ["+31", "NL", 21], ["+49", "DE", 19],
  ["+43", "AT", 20], ["+41", "CH", 8.1], ["+44", "GB", 20], ["+40", "RO", 19], ["+32", "BE", 6],
];
const normPhone = (p) => {
  let s = (p || "").replace(/[^\d+]/g, "").trim();
  if (s.startsWith("00")) s = "+" + s.slice(2);
  return s;
};
const availableVatRates = (phone) => {
  const p = normPhone(phone);
  if (!p.startsWith("+")) return [6, 21];
  for (const [prefix, code, rate] of [...PHONE_VAT_TABLE].sort((a, b) => b[0].length - a[0].length)) {
    if (p.startsWith(prefix)) {
      if (code === "BE") return [6, 21];
      if (code === "LU") return [3, 17];
      return [rate];
    }
  }
  return [21];
};

// Sous-total HTVA d'une ligne de coût : qty × PU, repli fourniture/MO puis `amount`.
const suppBase = (s) => {
  if (s.qty != null || s.unit_price != null) return num(s.qty) * num(s.unit_price);
  const sv = num(s.supply_htva), lv = num(s.labor_htva);
  return sv === 0 && lv === 0 ? num(s.amount) : sv + lv;
};
const lineBase = (s) => num(s.qty) * num(s.unit_price);
const lineVat = (s) => (lineBase(s) * (num(s.vat_rate) || 0)) / 100;

const recapOf = (c) => {
  const suppl = (c.supplements || []).reduce((s, x) => s + (x.operating ? 0 : suppBase(x)), 0);
  const dep = (c.deposits || []).reduce((s, x) => s + num(x.amount), 0);
  const toInvoice = num(c.amount) + suppl;
  const trips = num(c.trips_planned) * num(c.trip_cost);
  const measures = num(c.measures_planned) * num(c.measure_cost);
  const parking = num(c.parking_count) * num(c.parking_cost);
  const fuel = num(c.fuel_cost);
  const fonctTotal = trips + measures + parking + fuel; // frais de fonctionnement dédiés — informatif, jamais additionné aux suppléments
  const billableTtc = (c.supplements || []).reduce((s, x) => s + (x.operating ? 0 : lineBase(x) + lineVat(x)), 0);
  const operatingTtc = (c.supplements || []).reduce((s, x) => s + (x.operating ? lineBase(x) + lineVat(x) : 0), 0);
  return {
    suppl, dep, toInvoice, balance: toInvoice - dep, fonctTotal, billableTtc, operatingTtc,
    opex: { trips, measures, parking, fuel, total: fonctTotal },
  };
};

const COLUMNS = ["phase1", "phase2", "phase3", "reception"];
const GESTIONNAIRES = ["Brigitte", "Georges", "Maria", "Damien"];

const draftFrom = (c) => ({
  ...c,
  amount: c.amount == null ? "" : String(c.amount),
  trips_planned: String(c.trips_planned ?? 0),
  trip_cost: String(c.trip_cost ?? 0),
  measures_planned: String(c.measures_planned ?? 0),
  measure_cost: String(c.measure_cost ?? 0),
  parking_count: String(c.parking_count ?? 0),
  parking_cost: String(c.parking_cost ?? 0),
  fuel_cost: String(c.fuel_cost ?? 0),
  start_date: c.start_date || "",
  end_date: c.end_date || "",
  phase_dates: c.phase_dates || {},
  phase_end_dates: c.phase_end_dates || {},
  placeur: c.placeur || "",
  atelier_couture: c.atelier_couture || "",
  mesureur: c.mesureur || "",
  gestionnaire: c.gestionnaire || "",
  gestionnaire_phone: c.gestionnaire_phone || "",
  deposits: (c.deposits || []).map((d) => ({ amount: String(d.amount ?? ""), date: d.date || "", note: d.note || "" })),
  supplements: (c.supplements || []).map((s) => {
    const hasQ = s.qty != null || s.unit_price != null;
    return {
      label: s.label || "", phase: s.phase || c.status || "phase1",
      qty: hasQ ? String(s.qty ?? 1) : "1",
      unit_price: hasQ ? String(s.unit_price ?? 0) : String(s.amount != null ? s.amount : (num(s.supply_htva) + num(s.labor_htva)) || ""),
      vat_rate: s.vat_rate ? num(s.vat_rate) : 21,
      operating: !!s.operating,
      date: s.date || "", room: s.room || "",
    };
  }),
  end_phase: c.end_phase || null,
});

export default function Crm() {
  const { t } = useI18n();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // draft object
  const [showImport, setShowImport] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [newStart, setNewStart] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setChantiers(await api.listChantiers());
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openImport = async () => {
    setNewStart("");
    setShowImport(true);
    try { setQuotes(await api.listQuotes()); } catch (e) { /* noop */ }
  };

  const createFromQuote = async (quoteId) => {
    if (!newStart) return;
    setBusy(true);
    try {
      const base = { start_date: newStart };
      const c = await api.createChantier(quoteId ? { ...base, from_quote_id: quoteId } : base);
      setShowImport(false);
      setChantiers((p) => [c, ...p]);
      setEditing(draftFrom(c));
    } finally { setBusy(false); }
  };

  const move = async (c, status) => {
    const updated = await api.updateChantier(c.id, { status });
    setChantiers((p) => p.map((x) => (x.id === c.id ? updated : x)));
  };

  const save = async (opts = {}) => {
    const keepOpen = opts && opts.keepOpen === true;
    setBusy(true);
    try {
      const d = editing;
      const payload = {
        title: d.title || "",
        client_name: d.client_name || "",
        client_phone: d.client_phone || "",
        client_email: d.client_email || "",
        address: d.address || "",
        amount: d.amount === "" ? null : num(d.amount),
        status: d.status,
        trips_planned: parseInt(d.trips_planned || "0", 10) || 0,
        trip_cost: num(d.trip_cost),
        measures_planned: parseInt(d.measures_planned || "0", 10) || 0,
        measure_cost: num(d.measure_cost),
        parking_count: parseInt(d.parking_count || "0", 10) || 0,
        parking_cost: num(d.parking_cost),
        fuel_cost: num(d.fuel_cost),
        start_date: d.start_date || "",
        end_date: d.end_date || "",
        phase_dates: d.phase_dates || {},
        phase_end_dates: d.phase_end_dates || {},
        deposits: (d.deposits || []).map((x) => ({ amount: num(x.amount), date: x.date || "", note: x.note || "" })),
        supplements: (d.supplements || []).map((x) => {
          const q = num(x.qty), up = num(x.unit_price);
          return {
            label: x.label || "", phase: ["phase1", "phase2", "phase3", "reception"].includes(x.phase) ? x.phase : "phase1",
            category: "other", qty: q, unit_price: up,
            supply_htva: 0, labor_htva: 0,
            vat_rate: num(x.vat_rate) || 21,
            operating: !!x.operating,
            amount: q * up, date: x.date || "", room: x.room || "",
          };
        }),
        end_phase: d.end_phase || null,
      };
      if (isSuperadmin()) {
        payload.placeur = d.placeur || "";
        payload.atelier_couture = d.atelier_couture || "";
        payload.mesureur = d.mesureur || "";
        payload.gestionnaire = d.gestionnaire || "";
        payload.gestionnaire_phone = d.gestionnaire_phone || "";
      }
      const updated = await api.updateChantier(d.id, payload);
      setChantiers((p) => p.map((x) => (x.id === d.id ? updated : x)));
      if (keepOpen) { setEditing(updated); } else { setEditing(null); }
      return true;
    } catch (e) { setError(e.message); return false; } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.deleteChantier(editing.id);
      setChantiers((p) => p.filter((x) => x.id !== editing.id));
      setEditing(null);
    } finally { setBusy(false); }
  };

  const closeSite = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const updated = await api.updateChantier(editing.id, { status: "reception", end_phase: editing.status });
      setChantiers((p) => p.map((x) => (x.id === editing.id ? updated : x)));
      setEditing(updated);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl font-semibold text-ink">{t("crm_title")}</h1>
          <p className="mt-1 text-[14px] text-mute">{chantiers.length} · {t("dash_crm")}</p>
        </div>
        <button data-testid="crm-import-btn" onClick={openImport} className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-[14px] font-semibold text-white">
          <Icon name="user-plus" size={16} className="text-white" /> {t("crm_import")}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Icon name="loader" size={32} className="animate-spin text-brand" /></div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Icon name="alert-circle" size={36} className="text-err" />
          <p className="text-[14px] text-mute">{error}</p>
          <button onClick={load} className="text-[15px] font-semibold text-brand">{t("sum_retry")}</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {COLUMNS.map((col) => {
            const items = chantiers.filter((c) => c.status === col);
            return (
              <div key={col} data-testid={`crm-col-${col}`} className="flex flex-col rounded-2xl border border-line bg-paper-3/50 p-3">
                <div className="mb-3 flex items-center justify-between px-1">
                  <h3 className="text-[14px] font-semibold text-brand">{t(`crm_${col}`)}</h3>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[12px] font-semibold text-ink-3">{items.length}</span>
                </div>
                <div className="flex flex-col gap-2">
                  {items.length === 0 ? (
                    <p className="px-1 py-6 text-center text-[12px] text-mute">{t("crm_empty")}</p>
                  ) : items.map((c) => {
                    const rc = recapOf(c);
                    return (
                      <button key={c.id} data-testid={`crm-card-${c.id}`} onClick={() => setEditing(draftFrom(c))}
                        className="w-full rounded-xl border border-line bg-white p-3 text-left transition-shadow hover:shadow-sm">
                        <p className="truncate text-[14px] font-semibold text-ink">{c.title || c.client_name || "—"}</p>
                        {c.client_name && c.title ? <p className="truncate text-[12px] text-mute">{c.client_name}</p> : null}
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-3">
                          {c.amount != null ? <span className="font-semibold text-brand">{eur(rc.toInvoice)}</span> : null}
                          {rc.dep > 0 ? <span className="inline-flex items-center gap-1"><Icon name="wallet" size={11} /> {eur(rc.dep)}</span> : null}
                          {c.amount != null && rc.dep > 0 ? <span className="font-semibold text-brand-on">= {eur(rc.balance)}</span> : null}
                          {c.trips_planned ? <span className="inline-flex items-center gap-1"><Icon name="car" size={11} /> {c.trips_planned}</span> : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowImport(false)}>
          <div className="max-h-[80vh] w-full max-w-md overflow-hidden rounded-2xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-line px-5 py-4">
              <h3 className="text-[16px] font-semibold text-ink">{t("crm_import_pick")}</h3>
              <button data-testid="crm-import-close" onClick={() => setShowImport(false)}><Icon name="x" size={20} className="text-mute" /></button>
            </div>
            <div className="max-h-[52vh] overflow-y-auto p-3">
              <div className="mb-2 px-1">
                <label className="mb-1 block text-[13px] font-medium text-ink-3">{t("crm_start_date")}</label>
                <input data-testid="crm-create-start-date" type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2.5 text-[14px] text-ink" />
                <p className="mt-1 text-[11px] text-mute">{t("crm_create_date_hint")}</p>
              </div>
              {quotes.length === 0 ? (
                <p className="py-8 text-center text-[13px] text-mute">{t("crm_no_quotes")}</p>
              ) : quotes.map((q) => (
                <button key={q.id} data-testid={`crm-import-quote-${q.id}`} disabled={busy || !newStart} onClick={() => createFromQuote(q.id)}
                  className="flex w-full items-center justify-between gap-3 rounded-xl px-3 py-3 text-left hover:bg-paper-3 disabled:opacity-50">
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-semibold text-ink">{q.client_name}</p>
                    <p className="truncate text-[12px] text-mute">{q.client_email} · {q.client_phone || "—"}</p>
                  </div>
                  <Icon name="chevron-right" size={16} className="shrink-0 text-mute" />
                </button>
              ))}
            </div>
            <div className="border-t border-line p-3">
              <button data-testid="crm-create-manual" disabled={busy || !newStart} onClick={() => createFromQuote(null)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-tertiary py-3 text-[14px] font-semibold text-brand-on disabled:opacity-50">
                <Icon name="plus" size={16} className="text-brand" /> {t("crm_manual")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <EditModal draft={editing} setDraft={setEditing} t={t} busy={busy} onSave={save} onDelete={remove} onCloseSite={closeSite} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function EditModal({ draft, setDraft, t, busy, onSave, onDelete, onCloseSite, onClose }) {
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const [recMsg, setRecMsg] = useState("");
  const downloadReception = async () => {
    setRecMsg("");
    const ok = await onSave({ keepOpen: true });
    if (!ok) { setRecMsg(t("crm_save_first_recalc")); return; }
    window.open(fileUrlAuth(`/admin/chantiers/${draft.id}/reception-docx`), "_blank");
  };
  const downloadReceptionPdf = async () => {
    setRecMsg("");
    const ok = await onSave({ keepOpen: true });
    if (!ok) { setRecMsg(t("crm_save_first_recalc")); return; }
    window.open(fileUrlAuth(`/admin/chantiers/${draft.id}/reception-pdf`), "_blank");
  };
  const vatOptions = availableVatRates(draft.client_phone);
  const defaultVat = () => {
    const p1 = (draft.supplements || []).find((x) => (x.phase || "phase1") === "phase1" && x.vat_rate != null && x.vat_rate !== "");
    const v = p1 ? num(p1.vat_rate) : vatOptions[0];
    return vatOptions.includes(v) ? v : vatOptions[0];
  };
  // Date propre à la phase courante : Phase 1 = date de début ; autres phases = phase_dates[phase].
  const phaseDate = draft.status === "phase1" ? (draft.start_date || "") : ((draft.phase_dates || {})[draft.status] || "");
  const setPhaseDate = (v) => {
    if (draft.status === "phase1") set("start_date", v);
    else setDraft((d) => ({ ...d, phase_dates: { ...(d.phase_dates || {}), [d.status]: v } }));
  };
  // Date de fin optionnelle par phase : si renseignée, la phase peut être imprimée (réception possible).
  const phaseEndDate = (draft.phase_end_dates || {})[draft.status] || "";
  const setPhaseEndDate = (v) => setDraft((d) => ({ ...d, phase_end_dates: { ...(d.phase_end_dates || {}), [d.status]: v } }));
  // Enregistre d'abord, puis génère les documents Word + PDF (disponible dès qu'une date de phase est saisie).
  const recalcPrint = async () => {
    setRecMsg("");
    const ok = await onSave({ keepOpen: true });
    if (!ok) { setRecMsg(t("crm_save_first_recalc")); return; }
    window.open(fileUrlAuth(`/admin/chantiers/${draft.id}/reception-docx`), "_blank");
    window.open(fileUrlAuth(`/admin/chantiers/${draft.id}/reception-pdf`), "_blank");
  };
  const depTotal = useMemo(() => (draft.deposits || []).reduce((s, d) => s + (parseFloat(d.amount) || 0), 0), [draft.deposits]);
  const r = recapOf(draft);

  const setDep = (i, k, v) => setDraft((d) => ({ ...d, deposits: d.deposits.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addDep = () => setDraft((d) => ({ ...d, deposits: [...(d.deposits || []), { amount: "", date: "", note: "" }] }));
  const rmDep = (i) => setDraft((d) => ({ ...d, deposits: d.deposits.filter((_, j) => j !== i) }));
  const setSup = (i, k, v) => setDraft((d) => ({ ...d, supplements: d.supplements.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));
  const addSup = () => setDraft((d) => ({ ...d, supplements: [...(d.supplements || []), { label: "", phase: d.status || "phase1", qty: "1", unit_price: "", vat_rate: defaultVat(), operating: false, date: "", room: "" }] }));
  const rmSup = (i) => setDraft((d) => ({ ...d, supplements: d.supplements.filter((_, j) => j !== i) }));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div className="my-4 w-full max-w-lg rounded-2xl bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 flex items-center justify-between rounded-t-2xl border-b border-line bg-white px-5 py-4">
          <h3 className="text-[16px] font-semibold text-ink">{t("crm_details")}</h3>
          <button data-testid="crm-edit-close" onClick={onClose}><Icon name="x" size={20} className="text-mute" /></button>
        </div>
        <div className="p-5">
          {/* Move buttons */}
          <div className="mb-4 flex flex-wrap gap-1.5">
            {COLUMNS.map((col) => (
              <button key={col} data-testid={`crm-move-${col}`} onClick={() => set("status", col)}
                className={cn("rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors", draft.status === col ? "bg-brand text-white" : "bg-paper-3 text-ink-3")}>
                {t(`crm_${col}`)}
              </button>
            ))}
          </div>
          {draft.status !== "reception" && (
            <button data-testid="crm-close-site" disabled={busy} onClick={onCloseSite}
              className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border border-brand bg-brand-tertiary py-2.5 text-[13px] font-semibold text-brand disabled:opacity-60">
              <Icon name="flag" size={15} className="text-brand" /> {t("crm_close_site")}
            </button>
          )}

          {/* Recap financier */}
          <div data-testid="crm-recap" className="mb-4 rounded-xl bg-brand-tertiary p-3">
            <div className="mb-1.5 text-[12px] font-semibold text-brand">{t("crm_recap")}</div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_phase_costs_total")}</span><span className="font-semibold text-ink" data-testid="crm-recap-suppl">{eur(r.suppl)}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_to_invoice")}</span><span className="font-semibold text-ink">{eur(r.toInvoice)}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_deposits_total")}</span><span className="font-semibold text-ink">− {eur(r.dep)}</span></div>
            <div className="mt-1 flex justify-between border-t border-line pt-1 text-[13px]"><span className="font-semibold text-brand-on">{t("crm_balance")}</span><span className="font-bold text-brand" data-testid="crm-balance">{eur(r.balance)}</span></div>
          </div>

          <Field label={t("crm_title_field")} testId="crm-f-title" value={draft.title || ""} onChange={(e) => set("title", e.target.value)} />
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Field label={t("crm_name")} testId="crm-f-name" value={draft.client_name || ""} onChange={(e) => set("client_name", e.target.value)} />
            <Field label={t("crm_phone")} testId="crm-f-phone" value={draft.client_phone || ""} onChange={(e) => set("client_phone", e.target.value)} />
            <Field label={t("crm_email")} testId="crm-f-email" value={draft.client_email || ""} onChange={(e) => set("client_email", e.target.value)} />
            <Field label={t("crm_amount")} testId="crm-f-amount" inputMode="decimal" value={draft.amount || ""} onChange={(e) => set("amount", e.target.value)} />
          </div>
          <Field label={t("crm_address")} testId="crm-f-address" value={draft.address || ""} onChange={(e) => set("address", e.target.value)} />

          <h4 className="mb-2 mt-4 text-[13px] font-semibold text-brand">{t("crm_dates")}</h4>
          {!draft.start_date ? (
            <div data-testid="crm-start-warn" className="mb-2 flex items-center gap-2 rounded-lg border border-err/40 bg-err/5 px-3 py-2 text-[12px] font-medium text-err">
              <Icon name="alert-circle" size={14} className="shrink-0" /> {t("crm_start_required")}
            </div>
          ) : null}
          <Field label={draft.status === "phase1" ? t("crm_start_date") : `${t("crm_phase_date")} — ${t(`crm_${draft.status}`)}`} testId="crm-f-phase-date" type="date" value={phaseDate} onChange={(e) => setPhaseDate(e.target.value)} />
          <p className="-mt-1 mb-2 text-[11px] text-mute">{t("crm_start_date_hint")}</p>
          <Field label={`${t("crm_end_date")} — ${t(`crm_${draft.status}`)}`} testId="crm-f-phase-end-date" type="date" value={phaseEndDate} onChange={(e) => setPhaseEndDate(e.target.value)} />
          <p className="-mt-1 mb-2 text-[11px] text-mute">{t("crm_phase_end_hint")}</p>
          {(phaseDate || phaseEndDate) ? (
            <button data-testid="crm-recalc-print" disabled={busy} onClick={recalcPrint}
              className="mb-2 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-2.5 text-[13px] font-semibold text-white disabled:opacity-60">
              <Icon name="printer" size={15} className="text-white" /> {t("crm_recalc_print")}
            </button>
          ) : null}
          {recMsg && draft.status !== "reception" ? <div data-testid="crm-recalc-msg" className="mb-2 text-center text-[12px] font-semibold text-err">{recMsg}</div> : null}

          {isSuperadmin() && (
            <>
              <h4 className="mb-2 mt-4 text-[13px] font-semibold text-brand">{t("crm_assignment")}</h4>
              <div className="grid gap-x-4 sm:grid-cols-2">
                <div className="mb-3">
                  <label className="mb-1 block text-[13px] font-medium text-ink-3">{t("crm_gestionnaire")}</label>
                  <select data-testid="crm-f-gestionnaire" value={draft.gestionnaire || ""} onChange={(e) => set("gestionnaire", e.target.value)}
                    className="w-full rounded-xl border border-line bg-white px-3 py-2.5 text-[14px] text-ink">
                    <option value="">—</option>
                    {GESTIONNAIRES.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <Field label={t("crm_gestionnaire_phone")} testId="crm-f-gestionnaire-phone" value={draft.gestionnaire_phone || ""} onChange={(e) => set("gestionnaire_phone", e.target.value)} />
                <Field label={t("crm_placeur")} testId="crm-f-placeur" value={draft.placeur || ""} onChange={(e) => set("placeur", e.target.value)} />
                <Field label={t("crm_atelier")} testId="crm-f-atelier" value={draft.atelier_couture || ""} onChange={(e) => set("atelier_couture", e.target.value)} />
                <Field label={t("crm_mesureur")} testId="crm-f-mesureur" value={draft.mesureur || ""} onChange={(e) => set("mesureur", e.target.value)} />
              </div>
            </>
          )}

          <h4 className="mb-2 mt-4 text-[13px] font-semibold text-brand">{t("crm_costs")}</h4>
          <div className="grid gap-x-4 sm:grid-cols-3">
            <Field label={t("crm_trips")} testId="crm-f-trips" inputMode="numeric" value={draft.trips_planned || ""} onChange={(e) => set("trips_planned", e.target.value)} />
            <Field label={t("crm_trip_cost")} testId="crm-f-trip-cost" inputMode="decimal" value={draft.trip_cost || ""} onChange={(e) => set("trip_cost", e.target.value)} />
            <div className="hidden sm:block" />
            <Field label={t("crm_measures")} testId="crm-f-measures" inputMode="numeric" value={draft.measures_planned || ""} onChange={(e) => set("measures_planned", e.target.value)} />
            <Field label={t("crm_measure_cost")} testId="crm-f-measure-cost" inputMode="decimal" value={draft.measure_cost || ""} onChange={(e) => set("measure_cost", e.target.value)} />
            <div className="hidden sm:block" />
            <Field label={t("crm_parking_count")} testId="crm-f-parking-count" inputMode="numeric" value={draft.parking_count || ""} onChange={(e) => set("parking_count", e.target.value)} />
            <Field label={t("crm_parking")} testId="crm-f-parking" inputMode="decimal" value={draft.parking_cost || ""} onChange={(e) => set("parking_cost", e.target.value)} />
            <Field label={t("crm_fuel")} testId="crm-f-fuel" inputMode="decimal" value={draft.fuel_cost || ""} onChange={(e) => set("fuel_cost", e.target.value)} />
          </div>

          <div data-testid="crm-opex" className="mb-2 mt-2 rounded-xl border border-line bg-paper-2 p-3">
            <div className="mb-1.5 text-[12px] font-semibold text-brand">{t("crm_opex")}</div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_opex_trips")} ({draft.trips_planned || 0} × {eur(num(draft.trip_cost))})</span><span className="font-semibold text-ink" data-testid="crm-opex-trips">{eur(r.opex.trips)}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_opex_measures")} ({draft.measures_planned || 0} × {eur(num(draft.measure_cost))})</span><span className="font-semibold text-ink" data-testid="crm-opex-measures">{eur(r.opex.measures)}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_opex_parking")} ({draft.parking_count || 0} × {eur(num(draft.parking_cost))})</span><span className="font-semibold text-ink" data-testid="crm-opex-parking">{eur(r.opex.parking)}</span></div>
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_opex_fuel")}</span><span className="font-semibold text-ink">{eur(r.opex.fuel)}</span></div>
            <div className="mt-1 flex justify-between border-t border-line pt-1 text-[13px]"><span className="font-semibold text-brand-on">{t("crm_opex_total")}</span><span className="font-bold text-brand" data-testid="crm-opex-total">{eur(r.opex.total)}</span></div>
          </div>

          {/* Deposits */}
          <div className="mb-2 mt-4 flex items-center justify-between">
            <h4 className="text-[13px] font-semibold text-brand">{t("crm_deposits")}</h4>
            <span className="text-[12px] font-semibold text-ink-3">{t("crm_deposits_total")}: {Math.round(depTotal).toLocaleString("fr-FR")} €</span>
          </div>
          {(draft.deposits || []).map((d, i) => (
            <div key={i} className="mb-2 grid grid-cols-[1fr_1fr_auto] items-end gap-2">
              <Field label={t("crm_deposit_amount")} testId={`crm-dep-amount-${i}`} inputMode="decimal" value={d.amount} onChange={(e) => setDep(i, "amount", e.target.value)} />
              <Field label={t("crm_deposit_date")} testId={`crm-dep-date-${i}`} type="date" value={d.date} onChange={(e) => setDep(i, "date", e.target.value)} />
              <button data-testid={`crm-dep-rm-${i}`} onClick={() => rmDep(i)} className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-paper-3"><Icon name="trash-2" size={16} className="text-err" /></button>
            </div>
          ))}
          <button data-testid="crm-dep-add" onClick={addDep} className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-brand"><Icon name="plus" size={14} /> {t("crm_deposit_add")}</button>

          {/* Suppléments — indépendants par phase (saisie de la phase en cours uniquement) */}
          <h4 className="mb-2 mt-4 text-[13px] font-semibold text-brand">{t("crm_supplements")} — {t(`crm_${draft.status}`)}</h4>
          {(draft.supplements || []).map((s, i) => ({ s, i })).filter(({ s }) => (s.phase || "phase1") === draft.status).map(({ s, i }) => (
            <div key={i} data-testid={`crm-sup-${i}`} className="mb-2 rounded-xl border border-line p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex flex-wrap gap-1">
                  {COLUMNS.map((col) => (
                    <button key={col} data-testid={`crm-sup-phase-${i}-${col}`} onClick={() => setSup(i, "phase", col)}
                      className={cn("rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors", s.phase === col ? "bg-brand text-white" : "bg-paper-3 text-ink-3")}>
                      {t(`crm_${col}`)}
                    </button>
                  ))}
                </div>
                <button data-testid={`crm-sup-rm-${i}`} onClick={() => rmSup(i)} className="flex h-8 w-8 items-center justify-center rounded-lg bg-paper-3"><Icon name="trash-2" size={15} className="text-err" /></button>
              </div>
              <div className="grid grid-cols-2 gap-x-3">
                <div className="col-span-2">
                  <Field label={t("crm_supplement_label")} testId={`crm-sup-label-${i}`} value={s.label} onChange={(e) => setSup(i, "label", e.target.value)} />
                </div>
                <Field label={t("crm_sup_qty")} testId={`crm-sup-qty-${i}`} inputMode="decimal" value={s.qty} onChange={(e) => setSup(i, "qty", e.target.value)} />
                <Field label={t("crm_sup_unit")} testId={`crm-sup-unit-${i}`} inputMode="decimal" value={s.unit_price} onChange={(e) => setSup(i, "unit_price", e.target.value)} />
                <div className="mb-3">
                  <label className="mb-1 block text-[13px] font-medium text-ink-3">{t("crm_supplement_vat")}</label>
                  <select data-testid={`crm-sup-vat-${i}`} value={num(s.vat_rate)} onChange={(e) => setSup(i, "vat_rate", num(e.target.value))}
                    className="w-full rounded-xl border border-line bg-white px-3 py-2.5 text-[14px] text-ink">
                    {Array.from(new Set([...vatOptions, num(s.vat_rate)])).map((v) => <option key={v} value={v}>{`${v} %`}</option>)}
                  </select>
                </div>
                <div className="mb-3 flex items-end justify-end">
                  <div className="text-right">
                    <div className="text-[11px] text-mute">{s.operating ? t("crm_not_billed") : t("crm_sup_line")}</div>
                    <div className={cn("text-[14px] font-semibold", s.operating ? "text-mute line-through" : "text-brand")} data-testid={`crm-sup-line-${i}`}>{eur(lineBase(s) + lineVat(s))}</div>
                  </div>
                </div>
                <div className="col-span-2 mb-2">
                  <label className="mb-1 block text-[13px] font-medium text-ink-3">{t("crm_sup_type")}</label>
                  <div className="flex gap-2">
                    <button type="button" data-testid={`crm-sup-type-bill-${i}`} onClick={() => setSup(i, "operating", false)}
                      className={cn("flex-1 rounded-xl px-3 py-2 text-[12px] font-semibold transition-colors", !s.operating ? "bg-brand text-white" : "bg-paper-3 text-ink-3")}>{t("crm_type_billable")}</button>
                    <button type="button" data-testid={`crm-sup-type-op-${i}`} onClick={() => setSup(i, "operating", true)}
                      className={cn("flex-1 rounded-xl px-3 py-2 text-[12px] font-semibold transition-colors", s.operating ? "bg-amber-500 text-white" : "bg-paper-3 text-ink-3")}>{t("crm_mark_operating")}</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button data-testid="crm-sup-add" onClick={addSup} className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-brand"><Icon name="plus" size={14} /> {t("crm_supplement_add")}</button>

          {/* Deux totaux cumulés affichés séparément — jamais additionnés */}
          <div data-testid="crm-type-totals" className="mb-2 mt-2 rounded-xl border border-line bg-paper-2 p-3">
            <div className="flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_total_billable")}</span><span className="font-bold text-brand" data-testid="crm-total-billable">{eur(r.billableTtc)}</span></div>
            <div className="mt-0.5 flex justify-between text-[13px]"><span className="text-ink-3">{t("crm_total_operating")}</span><span className="font-bold text-amber-600" data-testid="crm-total-operating">{eur(r.operatingTtc)}</span></div>
          </div>

          {/* Récapitulatif de réception — lignes par phase, sous-totaux HTVA/TTC, grand total */}
          {(draft.status === "reception" || draft.end_phase) && (() => {
            let gH = 0, gT = 0;
            const sections = COLUMNS.map((ph) => {
              const lines = (draft.supplements || []).filter((x) => (x.phase || "phase1") === ph && !x.operating);
              if (!lines.length) return null;
              const hT = lines.reduce((s, x) => s + lineBase(x), 0);
              const tT = lines.reduce((s, x) => s + lineBase(x) + lineVat(x), 0);
              gH += hT; gT += tT;
              return { ph, lines, hT, tT };
            }).filter(Boolean);
            return (
              <div data-testid="crm-reception-recap" className="mb-2 mt-4 rounded-xl border border-brand/40 bg-brand-tertiary p-3">
                <div className="mb-1.5 text-[12px] font-semibold text-brand">{t("crm_recap_supp")}</div>
                {sections.map(({ ph, lines, hT, tT }) => (
                  <div key={ph} className="mb-2">
                    <div className="text-[12px] font-semibold text-brand-on">{t(`crm_${ph}`)}</div>
                    {lines.map((x, k) => (
                      <div key={k} className="flex justify-between gap-2 text-[13px]">
                        <span className="text-ink-3">{x.label || "—"} <span className="text-[11px] text-mute">{num(x.qty)} × {eur(num(x.unit_price))} · {num(x.vat_rate)}%</span></span>
                        <span className="font-semibold text-ink">{eur(lineBase(x) + lineVat(x))}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-line/60 pt-0.5 text-[12px] text-mute"><span>{t("crm_phase_subtotal")}</span><span>{eur(hT)} HTVA · {eur(tT)} TTC</span></div>
                  </div>
                ))}
                <div className="mt-1 flex justify-between border-t border-line pt-1 text-[13px]"><span className="font-semibold text-brand-on">{t("crm_recap_htva")}</span><span className="font-semibold text-ink" data-testid="crm-recap-htva">{eur(gH)}</span></div>
                <div className="flex justify-between text-[14px]"><span className="font-semibold text-brand-on">{t("crm_recap_ttc")}</span><span className="font-bold text-brand" data-testid="crm-recap-ttc">{eur(gT)}</span></div>
                <button data-testid="crm-reception-docx" disabled={busy} onClick={downloadReception}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-2.5 text-[13px] font-semibold text-white disabled:opacity-60">
                  <Icon name="file" size={15} className="text-white" /> {t("crm_download_reception")}
                </button>
                <button data-testid="crm-reception-pdf" disabled={busy} onClick={downloadReceptionPdf}
                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-brand bg-white py-2.5 text-[13px] font-semibold text-brand disabled:opacity-60">
                  <Icon name="file-text" size={15} className="text-brand" /> {t("crm_download_reception_pdf")}
                </button>
                {recMsg ? <div data-testid="crm-reception-msg" className="mt-2 text-center text-[12px] font-semibold text-err">{recMsg}</div> : null}
              </div>
            );
          })()}

          <div className="mt-6 flex items-center gap-2">
            <button data-testid="crm-save" disabled={busy} onClick={onSave} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand py-3 text-[14px] font-semibold text-white">
              <Icon name="check" size={16} className="text-white" /> {t("crm_save")}
            </button>
            <button data-testid="crm-delete" disabled={busy} onClick={onDelete} className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F5E4E4]">
              <Icon name="trash-2" size={18} className="text-err" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
